import { useEffect, useRef, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Header from './Header';
import InternalChatLauncher from './InternalChatLauncher';
import Sidebar from './Sidebar';
import { useChatContext } from '../context/useChatContext.js';

import './Layout.css';

const MOBILE_BREAKPOINT_QUERY = '(max-width: 767.98px)';

const detectMobileViewport = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
};

function Layout() {
  const [isMobileViewport, setIsMobileViewport] = useState(() => detectMobileViewport());
  const [collapsed, setCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const desktopCollapsedRef = useRef(false);
  const location = useLocation();
  const { isSuperAdmin, isGlobalAdmin, hasCompany, isAuthReady } = useChatContext();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const syncViewport = (event) => setIsMobileViewport(event.matches);

    setIsMobileViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncViewport);
      return () => mediaQuery.removeEventListener('change', syncViewport);
    }

    mediaQuery.addListener(syncViewport);
    return () => mediaQuery.removeListener(syncViewport);
  }, []);

  useEffect(() => {
    if (isMobileViewport) {
      setCollapsed(false);
      return;
    }
    setCollapsed(desktopCollapsedRef.current);
  }, [isMobileViewport]);

  useEffect(() => {
    if (isMobileViewport) return;
    desktopCollapsedRef.current = collapsed;
  }, [collapsed, isMobileViewport]);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsMobileSidebarOpen(false);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    if (!isMobileViewport || !isMobileSidebarOpen || typeof window === 'undefined') return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsMobileSidebarOpen(false);
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMobileSidebarOpen, isMobileViewport]);

  if (!isAuthReady) {
    return (
      <div className="layout-loading">
        <div className="layout-loading__content">Carregando sessão...</div>
      </div>
    );
  }

  if (isSuperAdmin === null) {
    return (
      <div className="layout-loading">
        <div className="layout-loading__content">Carregando perfil...</div>
      </div>
    );
  }

  const globalAdminRoutesWithoutCompany = ['/client-updates'];
  const superAdminRoutesWithoutCompany = ['/companyRegister', '/workflow-actions', '/webhooks'];
  const canAccessWithoutCompany =
    (isGlobalAdmin &&
      globalAdminRoutesWithoutCompany.some((path) => location.pathname.startsWith(path))) ||
    (isSuperAdmin &&
      superAdminRoutesWithoutCompany.some((path) => location.pathname.startsWith(path)));
  if (!hasCompany && !canAccessWithoutCompany) {
    const target = isSuperAdmin ? '/companyRegister' : isGlobalAdmin ? '/client-updates' : '/login';
    return <Navigate to={target} replace />;
  }

  const contentClass = `main-content ${isMobileViewport ? 'main-content--mobile' : collapsed ? 'main-content--collapsed' : 'main-content--expanded'}`;

  return (
    <div className="layout-container">
      {isMobileViewport && (
        <button
          type="button"
          className="mobile-sidebar-toggle"
          aria-label={isMobileSidebarOpen ? 'Fechar menu lateral' : 'Abrir menu lateral'}
          aria-expanded={isMobileSidebarOpen}
          onClick={() => setIsMobileSidebarOpen((prev) => !prev)}
        >
          {isMobileSidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      )}

      {isMobileViewport && isMobileSidebarOpen && (
        <button
          type="button"
          className="mobile-sidebar-backdrop"
          aria-label="Fechar menu lateral"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        isMobile={isMobileViewport}
        isMobileOpen={isMobileSidebarOpen}
        onRequestClose={() => setIsMobileSidebarOpen(false)}
      />
      <div className={contentClass}>
        <Header collapsed={collapsed} isMobile={isMobileViewport} />
        <main className="page">
          <Outlet />
        </main>
      </div>
      <InternalChatLauncher />
    </div>
  );

}

export default Layout;
